import {
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Typography,
  Button,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Device } from '../../../store/reducers/deviceReducers';
import { useDispatch } from 'react-redux';
import { updateDevice } from '../../../store/actions/deviceActions';
import deviceApi from '../../../api/devices';

export const DEFAULT_REPORTING_HOURS = 24;
export const DEFAULT_SAMPLING_HOURS = 12;

const HourField = ({
  label,
  description,
  value,
  edit,
  onChange,
}: {
  label: string;
  description: string;
  value: number;
  edit: boolean;
  onChange: (next: number) => void;
}) => (
  <Grid item xs={12} md={6}>
    <Typography variant='body2' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
      {description}
    </Typography>
    {edit ? (
      <Stack direction='row' spacing={1} alignItems='center'>
        <TextField
          size='small'
          type='number'
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          inputProps={{ min: 1 }}
          sx={{ width: 100 }}
        />
        <Typography variant='body2' color='text.secondary'>
          hours
        </Typography>
      </Stack>
    ) : (
      <Typography variant='body1' fontWeight='medium'>
        Every {value} hours
      </Typography>
    )}
  </Grid>
);

const DeviceSchedule = ({ device }: { device: Device }) => {
  const dispatch = useDispatch();
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reporting, setReporting] = useState(device.reporting_in_hours ?? DEFAULT_REPORTING_HOURS);
  const [sampling, setSampling] = useState(device.sampling_in_hours ?? DEFAULT_SAMPLING_HOURS);

  useEffect(() => {
    setReporting(device.reporting_in_hours ?? DEFAULT_REPORTING_HOURS);
    setSampling(device.sampling_in_hours ?? DEFAULT_SAMPLING_HOURS);
  }, [device.reporting_in_hours, device.sampling_in_hours]);

  const handleEdit = async () => {
    if (!edit) {
      setEdit(true);
      return;
    }

    const nextReporting = Number(reporting);
    const nextSampling = Number(sampling);
    if (nextReporting <= 0 || nextSampling <= 0) {
      return;
    }

    setLoading(true);
    try {
      const response = await deviceApi.putDevice(device.serial, {
        reporting_in_hours: nextReporting,
        sampling_in_hours: nextSampling,
      })();
      dispatch({
        type: updateDevice.success,
        payload: response.data,
      });
      setEdit(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setReporting(device.reporting_in_hours ?? DEFAULT_REPORTING_HOURS);
    setSampling(device.sampling_in_hours ?? DEFAULT_SAMPLING_HOURS);
    setEdit(false);
  };

  return (
    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Stack direction='row' alignItems='center' spacing={1}>
              <ScheduleIcon color='primary' />
              <Typography variant='h6'>Reporting & Sampling</Typography>
            </Stack>
            <Stack direction='row' spacing={1}>
              {edit && (
                <Button size='small' variant='outlined' onClick={handleCancel} disabled={loading}>
                  Cancel
                </Button>
              )}
              <Button
                startIcon={edit ? <SaveIcon /> : <EditIcon />}
                size='small'
                variant='outlined'
                disabled={loading}
                onClick={() => handleEdit()}
              >
                {edit ? 'Save' : 'Edit'}
              </Button>
            </Stack>
          </Stack>
          <Divider />
          <Grid container spacing={2}>
            <HourField
              label='Reporting interval'
              description='How often the device uploads data'
              value={reporting}
              edit={edit}
              onChange={setReporting}
            />
            <HourField
              label='Sampling interval'
              description='How often the device takes a reading'
              value={sampling}
              edit={edit}
              onChange={setSampling}
            />
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DeviceSchedule;
